import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useRestoreZakazka, useUpdateZakazka } from "@/api/hooks/useZakazky";
import type { StavZakazky } from "@/api/schemas";
import { cn } from "@/lib/utils";

/**
 * Fajka „Kalkulácia" (KOLO 239, do KOLA 238 „Faktúra"). User: *„keď dáš
 * kalkulované, tak sa automaticky dá zákazka do archívu"*.
 *
 * Zaškrtnutie sa spýta a server zákazku archivuje (stav Dokončená
 * + archív, `PATCH {fakturovana: true}`); toast má **Obnoviť**.
 * Odškrtnutie (zákazka obnovená z Archívu) stav nemení — Dokončenú
 * nastavuje človek. Archivovanú zákazku server nezmení (409), fajka je
 * vtedy len na čítanie.
 */
export function KalkulaciaFajka({
  zakazka,
  archivovana = false,
  stitok = false,
  className,
  onArchivovana,
}: {
  zakazka: {
    id: string;
    cislo_zakazky: string;
    fakturovana?: boolean;
    stav?: StavZakazky;
  };
  archivovana?: boolean;
  /** Nápis „Kalkulácia" vedľa fajky — všade, len na mobile (zoznam bez
   *  hlavičky stĺpca), alebo len od `sm` (hlavička — na mobile by zobral
   *  miesto názvu zákazky). */
  stitok?: boolean | "mobil" | "sm";
  className?: string;
  /** Po archivovaní — editor odíde na zoznam. */
  onArchivovana?: () => void;
}): JSX.Element {
  const update = useUpdateZakazka();
  const obnov = useRestoreZakazka();
  const [checked, setChecked] = useState(!!zakazka.fakturovana);
  const [potvrdit, setPotvrdit] = useState(false);

  useEffect(() => {
    setChecked(!!zakazka.fakturovana);
  }, [zakazka.fakturovana]);

  async function uloz(hodnota: boolean): Promise<boolean> {
    setChecked(hodnota);
    try {
      await update.mutateAsync({
        id: zakazka.id,
        data: { fakturovana: hodnota },
      });
      return true;
    } catch (err) {
      setChecked(!hodnota);
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
      return false;
    }
  }

  async function obnovit(dismiss: () => void): Promise<void> {
    dismiss();
    try {
      await obnov.mutateAsync(zakazka.id);
      toast({
        title: "Zákazka obnovená",
        description: `${zakazka.cislo_zakazky} je späť medzi zákazkami (kalkulácia ostala zaškrtnutá).`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Obnovenie zlyhalo",
        description: extractErrorMessage(err),
      });
    }
  }

  async function kalkulovane(): Promise<void> {
    setPotvrdit(false);
    if (!(await uloz(true))) return;
    const t = toast({
      title: "Kalkulované — zákazka je v Archíve",
      duration: 10000,
      description: (
        <span className="flex items-center gap-3">
          <span>{zakazka.cislo_zakazky}</span>
          <button
            type="button"
            className="rounded-md border px-2 py-0.5 text-xs font-medium hover:bg-accent"
            onClick={() => void obnovit(t.dismiss)}
          >
            Obnoviť
          </button>
        </span>
      ),
    });
    onArchivovana?.();
  }

  const title = archivovana
    ? "V Archíve — kalkuláciu zmeníš po obnovení zákazky"
    : checked
      ? "Kalkulácia hotová"
      : "Kalkulácia hotová — zákazka pôjde do Archívu";

  return (
    <>
      <label
        className={cn(
          "flex items-center gap-2",
          archivovana ? "cursor-default" : "cursor-pointer",
          className,
        )}
        title={title}
      >
        {stitok ? (
          <span
            className={cn(
              "text-xs text-muted-foreground",
              stitok === "mobil" && "md:hidden",
              stitok === "sm" && "hidden sm:inline",
            )}
          >
            Kalkulácia
          </span>
        ) : null}
        <input
          type="checkbox"
          checked={checked}
          disabled={archivovana || update.isPending}
          onChange={(e) => {
            if (e.target.checked) setPotvrdit(true);
            else void uloz(false);
          }}
          aria-label={`Kalkulácia ${zakazka.cislo_zakazky}`}
          className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-default"
        />
      </label>

      <Dialog open={potvrdit} onOpenChange={setPotvrdit}>
        <DialogContent
          className="max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Kalkulácia hotová?</DialogTitle>
            <DialogDescription>
              Zákazka {zakazka.cislo_zakazky} sa označí ako kalkulovaná a
              presunie sa do Archívu
              {zakazka.stav && zakazka.stav !== "dokoncena"
                ? " — stav sa nastaví na Dokončená"
                : ""}
              . Z Archívu sa dá obnoviť.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPotvrdit(false)}>
              Zrušiť
            </Button>
            <Button onClick={() => void kalkulovane()}>
              Kalkulované — do Archívu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
